import { useEffect, useMemo } from "react";
import { useLocation, useParams } from "react-router-dom";
import { useParentChild } from "@/contexts/ParentChildContext";
import { useAuth } from "@/contexts/AuthContext";

/**
 * Path prefix → Turkish label used in the browser tab title.
 * The router works on Turkish paths already so this stays in sync with the sidebar labels.
 */
const ROUTE_LABELS = [
  ["/panel", "Panel"],
  ["/odevler/yeni", "Yeni Ödev"],
  ["/odevler", "Ödevler"],
  ["/takvim", "Takvim"],
  ["/ai/ogretmen", "AI Öğretmen"],
  ["/ai/kontrol", "AI Ödev Kontrol"],
  ["/ai/koc", "AI Çalışma Koçu"],
  ["/ai/sinav-uret", "AI Sınav Üret"],
  ["/meydan-okuma", "Meydan Okuma"],
  ["/sinavlar", "Sınavlar"],
  ["/canli-dersler", "Canlı Dersler"],
  ["/odak", "Odak Zamanlayıcı"],
  ["/defter", "Not Defterim"],
  ["/basarilar", "Başarılarım"],
  ["/mesajlar", "Mesajlar"],
  ["/duyurular", "Duyurular"],
  ["/analitik", "Analitik"],
  ["/bildirimler", "Bildirimler"],
  ["/profil", "Profil"],
  ["/2fa", "Güvenlik / 2FA"],
  ["/raporlar", "Raporlar"],
  ["/kullanicilar/toplu-davet", "Toplu Davet"],
  ["/kullanicilar", "Kullanıcılar"],
  ["/siniflar", "Sınıflar"],
  ["/okullar", "Okullar"],
  ["/soru-bankasi", "Soru Bankası"],
  ["/yoklama", "Yoklama"],
  ["/veli-uyarilari", "Veli Uyarıları"],
  ["/konu-bosluklari", "Konu Boşlukları"],
  ["/gorusme-randevulari", "Görüşme Randevuları"],
  ["/gorusmelerim", "Görüşmelerim"],
  ["/gelismis-analitik", "Gelişmiş Analitik"],
  ["/denetim", "Denetim Kaydı"],
  ["/abonelik", "Abonelik"],
  ["/platform/panel", "Platform · Panel"],
  ["/platform/kiracilar", "Platform · Kiracılar"],
  ["/platform/satis", "Platform · Satış CRM"],
  ["/platform/finans", "Platform · Finans"],
  ["/platform/destek", "Platform · Destek"],
  ["/platform/surumler", "Platform · Sürümler"],
  ["/platform/duyurular", "Platform · Duyurular"],
  ["/platform/saglik-uyarilari", "Platform · Sağlık"],
  ["/platform/denetim", "Platform · Denetim"],
];

function labelForPath(path) {
  for (const [prefix, label] of ROUTE_LABELS) {
    if (path === prefix || path.startsWith(prefix + "/")) return label;
  }
  return "";
}

/**
 * Sets document.title based on:
 *  - Base: "Leranio"
 *  - Current route (mapped to Turkish label)
 *  - Parent's active child first name (only when user is a parent and a child is selected)
 *
 * Examples:
 *   "Leranio · Ali · Ödevler"          (parent viewing Ali)
 *   "Leranio · Ödevler"                (parent 'Tümü' or non-parent)
 *   "Leranio — Öğrenmeyi Akıllı Kılın" (landing / unknown route)
 */
export default function PageTitleUpdater() {
  const { user } = useAuth();
  const { isParent, activeChild } = useParentChild();
  const location = useLocation();

  const title = useMemo(() => {
    const routeLabel = labelForPath(location.pathname);
    if (!user) {
      return routeLabel ? `Leranio · ${routeLabel}` : "Leranio — Öğrenmeyi Akıllı Kılın";
    }
    const parts = ["Leranio"];
    if (isParent && activeChild?.name) {
      parts.push(activeChild.name.split(" ")[0]);
    }
    if (routeLabel) parts.push(routeLabel);
    return parts.join(" · ");
  }, [user, isParent, activeChild, location.pathname]);

  useEffect(() => {
    document.title = title;
  }, [title]);

  // Sadece document.title güncelliyoruz — DOM'a hiçbir şey basmıyoruz.
  useParams();
  return null;
}
